/**
 * Secure License Storage with AES-256-GCM encryption
 * Handles encrypted storage and retrieval of license data
 */

import type { LicenseInfo } from '$lib/types/subscription';
import type { SecureLicenseStorage } from './interfaces';
import { sha256 } from '$lib/utils/crypto';

/**
 * Encrypted license storage entry
 */
interface EncryptedLicenseEntry {
  encryptedData: string;
  iv: string;
  tag: string;
  salt: string;
  timestamp: number;
  version: number;
}

/**
 * Storage integrity record
 */
interface StorageIntegrityRecord {
  licenseCount: number;
  lastUpdated: number;
  checksum: string;
  version: number;
}

export class SecureLicenseStorageImpl implements SecureLicenseStorage {
  private readonly STORAGE_PREFIX = 'secure_license_';
  private readonly INTEGRITY_KEY = 'license_storage_integrity';
  private readonly ENCRYPTION_VERSION = 1;
  private readonly AES_KEY_LENGTH = 32; // 256 bits
  private readonly IV_LENGTH = 12; // 96 bits for GCM
  private readonly SALT_LENGTH = 16; // 128 bits
  private readonly TAG_LENGTH = 16; // 128 bits

  // Cached encryption key
  private masterKey: CryptoKey | null = null;
  
  constructor() {
    // Check if in demo mode or iframe context
    const urlParams = new URLSearchParams(window.location.search);
    const isDemoMode = urlParams.get('demo') === 'true';
    const isInIframe = window !== window.parent;

    // Skip initialization in demo mode or iframe to avoid cross-origin storage issues
    if (!isDemoMode && !isInIframe) {
      this.initializeStorage();
    }
  }

  /**
   * Initialize storage and perform integrity check
   */
  private async initializeStorage(): Promise<void> {
    try {
      // Verify storage integrity on startup
      const isValid = await this.verifyIntegrity();
      if (!isValid) {
        console.warn('License storage integrity check failed - some data may be corrupted');
      }
    } catch (error) {
      console.error('Failed to initialize secure license storage:', error);
    }
  }

  /**
   * Store encrypted license data
   */
  async storeLicense(licenseKey: string, licenseInfo: LicenseInfo): Promise<void> {
    try {
      // Generate encryption materials
      const salt = this.generateRandomBytes(this.SALT_LENGTH);
      const iv = this.generateRandomBytes(this.IV_LENGTH);
      
      // Derive encryption key
      const encryptionKey = await this.deriveKey(this.getMasterPassword(), salt);
      
      // Prepare data for encryption
      const licenseData = {
        ...licenseInfo,
        // Convert dates to ISO strings for serialization
        purchaseDate: licenseInfo.purchaseDate?.toISOString ? licenseInfo.purchaseDate.toISOString() : licenseInfo.purchaseDate,
        expiresAt: licenseInfo.expiresAt?.toISOString ? licenseInfo.expiresAt.toISOString() : licenseInfo.expiresAt,
        lastValidatedAt: licenseInfo.lastValidatedAt?.toISOString ? licenseInfo.lastValidatedAt.toISOString() : new Date().toISOString(),
        devices: (licenseInfo.devices || []).map(device => ({
          ...device,
          activatedAt: device.activatedAt?.toISOString ? device.activatedAt.toISOString() : device.activatedAt,
          lastActiveAt: device.lastActiveAt?.toISOString ? device.lastActiveAt.toISOString() : device.lastActiveAt
        }))
      };

      // Encrypt the license data
      const plaintext = new TextEncoder().encode(JSON.stringify(licenseData));
      const encrypted = await crypto.subtle.encrypt(
        {
          name: 'AES-GCM',
          iv: iv,
          tagLength: this.TAG_LENGTH * 8
        },
        encryptionKey,
        plaintext
      );

      // Extract tag from encrypted data
      const encryptedArray = new Uint8Array(encrypted);
      const encryptedData = encryptedArray.slice(0, -this.TAG_LENGTH);
      const tag = encryptedArray.slice(-this.TAG_LENGTH);

      // Create storage entry
      const entry: EncryptedLicenseEntry = {
        encryptedData: this.arrayBufferToBase64(encryptedData.buffer || encryptedData),
        iv: this.arrayBufferToBase64(iv),
        tag: this.arrayBufferToBase64(tag.buffer || tag),
        salt: this.arrayBufferToBase64(salt),
        timestamp: Date.now(),
        version: this.ENCRYPTION_VERSION
      };

      // Store encrypted entry
      const storageKey = this.getStorageKey(licenseKey);
      localStorage.setItem(storageKey, JSON.stringify(entry));

      // Update integrity record
      await this.updateIntegrityRecord();

    } catch (error) {
      throw new Error(`Failed to store license securely: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Retrieve and decrypt license data
   */
  async getLicense(licenseKey: string): Promise<LicenseInfo | null> {
    try {
      const storageKey = this.getStorageKey(licenseKey);
      const stored = localStorage.getItem(storageKey);
      
      if (!stored) {
        return null;
      }

      const entry: EncryptedLicenseEntry = JSON.parse(stored);
      
      // Verify version compatibility
      if (entry.version !== this.ENCRYPTION_VERSION) {
        console.warn(`License storage version mismatch: ${entry.version} vs ${this.ENCRYPTION_VERSION}`);
        // Could implement migration logic here
        return null;
      }

      // Derive decryption key
      const salt = this.base64ToArrayBuffer(entry.salt);
      const decryptionKey = await this.deriveKey(this.getMasterPassword(), salt);

      // Prepare encrypted data for decryption
      const encryptedData = this.base64ToArrayBuffer(entry.encryptedData);
      const tag = this.base64ToArrayBuffer(entry.tag);
      const iv = this.base64ToArrayBuffer(entry.iv);
      
      // Combine encrypted data and tag
      const encryptedWithTag = new Uint8Array(encryptedData.byteLength + tag.byteLength);
      encryptedWithTag.set(new Uint8Array(encryptedData));
      encryptedWithTag.set(new Uint8Array(tag), encryptedData.byteLength);

      // Decrypt
      const decrypted = await crypto.subtle.decrypt(
        {
          name: 'AES-GCM',
          iv: iv,
          tagLength: this.TAG_LENGTH * 8
        },
        decryptionKey,
        encryptedWithTag
      );

      // Parse decrypted data
      const decryptedText = new TextDecoder().decode(decrypted);
      const licenseData = JSON.parse(decryptedText);

      // Convert ISO strings back to dates
      return {
        ...licenseData,
        purchaseDate: new Date(licenseData.purchaseDate),
        expiresAt: licenseData.expiresAt ? new Date(licenseData.expiresAt) : undefined,
        lastValidatedAt: new Date(licenseData.lastValidatedAt),
        devices: licenseData.devices.map((device: any) => ({
          ...device,
          activatedAt: new Date(device.activatedAt),
          lastActiveAt: new Date(device.lastActiveAt)
        }))
      } as LicenseInfo;

    } catch (error) {
      console.error(`Failed to retrieve license ${licenseKey}:`, error);
      return null;
    }
  }

  /**
   * Remove license data
   */
  async removeLicense(licenseKey: string): Promise<void> {
    try {
      const storageKey = this.getStorageKey(licenseKey);
      localStorage.removeItem(storageKey);
      await this.updateIntegrityRecord();
    } catch (error) {
      console.error(`Failed to remove license ${licenseKey}:`, error);
    }
  }

  /**
   * Check if license exists in storage
   */
  async hasLicense(licenseKey: string): Promise<boolean> {
    const storageKey = this.getStorageKey(licenseKey);
    return localStorage.getItem(storageKey) !== null;
  }

  /**
   * List all stored license keys
   */
  async listLicenseKeys(): Promise<string[]> {
    const keys: string[] = [];
    
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(this.STORAGE_PREFIX)) {
        const licenseKey = key.substring(this.STORAGE_PREFIX.length);
        keys.push(licenseKey);
      }
    }
    
    return keys;
  }

  /**
   * Clear all license data
   */
  async clearAllLicenses(): Promise<void> {
    const keys = await this.listLicenseKeys();
    
    for (const licenseKey of keys) {
      await this.removeLicense(licenseKey);
    }
    
    // Clear integrity record
    localStorage.removeItem(this.INTEGRITY_KEY);
  }

  /**
   * Verify storage integrity
   */
  async verifyIntegrity(): Promise<boolean> {
    try {
      const integrityRecord = this.getIntegrityRecord();
      if (!integrityRecord) {
        // No integrity record - first time use or corrupted
        await this.updateIntegrityRecord();
        return true;
      }

      // Check license count
      const currentKeys = await this.listLicenseKeys();
      if (currentKeys.length !== integrityRecord.licenseCount) {
        console.warn(`License count mismatch: ${currentKeys.length} vs ${integrityRecord.licenseCount}`);
        return false;
      }

      // Verify checksum
      const currentChecksum = await this.calculateStorageChecksum();
      if (currentChecksum !== integrityRecord.checksum) {
        console.warn('Storage checksum mismatch');
        return false;
      }

      return true;
    } catch (error) {
      console.error('Integrity verification failed:', error);
      return false;
    }
  }

  /**
   * Private helper methods
   */

  private getStorageKey(licenseKey: string): string {
    return `${this.STORAGE_PREFIX}${licenseKey}`;
  }

  private getMasterPassword(): string {
    // In a real application, this would be derived from user authentication
    // or stored in a secure manner. For now, using a combination of factors.
    const factors = [
      navigator.userAgent,
      location.hostname,
      'korean-grammar-checker-license-key'
    ];
    
    return factors.join('|');
  }

  private async deriveKey(password: string, salt: ArrayBuffer): Promise<CryptoKey> {
    // Import password as raw key material
    const passwordBuffer = new TextEncoder().encode(password);
    const keyMaterial = await crypto.subtle.importKey(
      'raw',
      passwordBuffer,
      { name: 'PBKDF2' },
      false,
      ['deriveKey']
    );

    // Derive AES key using PBKDF2
    return await crypto.subtle.deriveKey(
      {
        name: 'PBKDF2',
        salt: salt,
        iterations: 100000, // High iteration count for security
        hash: 'SHA-256'
      },
      keyMaterial,
      { name: 'AES-GCM', length: 256 },
      false,
      ['encrypt', 'decrypt']
    );
  }

  private generateRandomBytes(length: number): ArrayBuffer {
    const bytes = new Uint8Array(length);
    crypto.getRandomValues(bytes);
    return bytes.buffer;
  }

  private arrayBufferToBase64(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  }

  private base64ToArrayBuffer(base64: string): ArrayBuffer {
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes.buffer;
  }

  private getIntegrityRecord(): StorageIntegrityRecord | null {
    const stored = localStorage.getItem(this.INTEGRITY_KEY);
    if (!stored) return null;
    
    try {
      return JSON.parse(stored);
    } catch (error) {
      console.error('Failed to parse integrity record:', error);
      return null;
    }
  }

  private async updateIntegrityRecord(): Promise<void> {
    const licenseKeys = await this.listLicenseKeys();
    const checksum = await this.calculateStorageChecksum();
    
    const record: StorageIntegrityRecord = {
      licenseCount: licenseKeys.length,
      lastUpdated: Date.now(),
      checksum,
      version: this.ENCRYPTION_VERSION
    };

    localStorage.setItem(this.INTEGRITY_KEY, JSON.stringify(record));
  }

  private async calculateStorageChecksum(): Promise<string> {
    const licenseKeys = await this.listLicenseKeys();
    licenseKeys.sort(); // Ensure consistent ordering
    
    const data = licenseKeys.join('|');
    
    // Use crypto utility with fallback for Safari
    const hashHex = await sha256(data);
    
    // Convert hex to base64-like format for compatibility
    return hashHex.substring(0, 32); // Use first 32 chars as checksum
  }
}